import { ClipId, SpriteId } from '@src/ecs/components';

/** One atlas frame in a sprite's frame table. */
export interface FrameInfo {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  /** Source pixel size. */
  w: number;
  h: number;
  /** Pivot offset from the frame center, pixels, y down. */
  ox: number;
  oy: number;
  /** Pixels per world unit at Sprite.scale = 1. */
  ppu: number;
}

export interface ClipInfo {
  /** Index of the first frame in the sprite's frame table. */
  start: number;
  len: number;
  fps: number;
  loop: boolean;
  /** Seconds for one pass. */
  duration: number;
}

export interface AtlasFrameJson {
  x: number;
  y: number;
  w: number;
  h: number;
  ox?: number;
  oy?: number;
}

export interface AtlasAnimJson {
  frames: string[];
  fps: number;
  loop: boolean;
  ppu: number;
}

export interface AtlasManifestJson {
  frames: Record<string, AtlasFrameJson>;
  anims?: Record<string, AtlasAnimJson>;
  meta: { size: number; padding: number };
}

/** Atlas clip names per sprite and clip. Missing entries fall back to Idle. */
const CLIP_NAMES: Readonly<Record<SpriteId, Partial<Record<ClipId, string>>>> = {
  [SpriteId.Player]: { [ClipId.Idle]: 'player_idle', [ClipId.Run]: 'player_run', [ClipId.Cast]: 'player_cast', [ClipId.Hurt]: 'player_hurt', [ClipId.Death]: 'player_death' },
  [SpriteId.Imp]: { [ClipId.Idle]: 'imp_idle', [ClipId.Run]: 'imp_run', [ClipId.Death]: 'imp_death' },
  [SpriteId.Golem]: { [ClipId.Idle]: 'golem_idle', [ClipId.Run]: 'golem_walk', [ClipId.Hurt]: 'golem_hurt', [ClipId.Death]: 'golem_death' },
  [SpriteId.Skull]: { [ClipId.Idle]: 'skull_idle', [ClipId.Run]: 'skull_run', [ClipId.Death]: 'skull_death' },
  [SpriteId.Slime]: { [ClipId.Idle]: 'slime_idle', [ClipId.Run]: 'slime_run', [ClipId.Death]: 'slime_death' },
  [SpriteId.Bat]: { [ClipId.Idle]: 'bat_idle', [ClipId.Run]: 'bat_run', [ClipId.Death]: 'bat_death' },
  [SpriteId.Gargoyle]: { [ClipId.Idle]: 'gargoyle_idle', [ClipId.Cast]: 'gargoyle_cast', [ClipId.Hurt]: 'gargoyle_hurt', [ClipId.Death]: 'gargoyle_death' },
  // Second-wave guests. Each has its own sheet name in the manifest; until that art is
  // generated, ART_FALLBACK borrows a cousin's frames and the spawn tint carries the read.
  [SpriteId.Wraith]: { [ClipId.Idle]: 'wraith_idle', [ClipId.Run]: 'wraith_run', [ClipId.Death]: 'wraith_death' },
  [SpriteId.Hexer]: { [ClipId.Idle]: 'hexer_idle', [ClipId.Run]: 'hexer_run', [ClipId.Cast]: 'hexer_cast', [ClipId.Death]: 'hexer_death' },
  [SpriteId.Cinder]: { [ClipId.Idle]: 'cinder_idle', [ClipId.Run]: 'cinder_run', [ClipId.Death]: 'cinder_death' },
  [SpriteId.Blight]: { [ClipId.Idle]: 'blight_idle', [ClipId.Run]: 'blight_run', [ClipId.Death]: 'blight_death' },
  [SpriteId.Brute]: { [ClipId.Idle]: 'brute_idle', [ClipId.Run]: 'brute_run', [ClipId.Hurt]: 'brute_hurt', [ClipId.Death]: 'brute_death' },
  [SpriteId.Spitter]: { [ClipId.Idle]: 'spitter_idle', [ClipId.Cast]: 'spitter_cast', [ClipId.Hurt]: 'spitter_hurt', [ClipId.Death]: 'spitter_death' },
  [SpriteId.Mimic]: { [ClipId.Idle]: 'mimic_idle', [ClipId.Run]: 'mimic_run', [ClipId.Death]: 'mimic_death' },
  [SpriteId.PickupHeart]: { [ClipId.Idle]: 'pickup_heart' },
  [SpriteId.Portal]: { [ClipId.Idle]: 'portal_swirl', [ClipId.Death]: 'portal_collapse' },
  [SpriteId.Dragon]: { [ClipId.Idle]: 'dragon_fly', [ClipId.Run]: 'dragon_fly', [ClipId.Cast]: 'dragon_breathe' },
  [SpriteId.BossLich]: { [ClipId.Idle]: 'boss_lich_idle', [ClipId.Cast]: 'boss_lich_cast', [ClipId.Hurt]: 'boss_lich_hurt', [ClipId.Death]: 'boss_lich_death' },
  [SpriteId.BossDruid]: { [ClipId.Idle]: 'boss_druid_idle', [ClipId.Cast]: 'boss_druid_cast', [ClipId.Hurt]: 'boss_druid_hurt', [ClipId.Death]: 'boss_druid_death' },
  [SpriteId.BossMagus]: { [ClipId.Idle]: 'boss_magus_idle', [ClipId.Cast]: 'boss_magus_cast', [ClipId.Hurt]: 'boss_magus_hurt', [ClipId.Death]: 'boss_magus_death' },
  [SpriteId.BossBolt]: { [ClipId.Idle]: 'fx_rune_bolt_loop' },
  [SpriteId.BossOrb]: { [ClipId.Idle]: 'fx_rune_orb_loop' },
  [SpriteId.BossWisp]: { [ClipId.Idle]: 'fx_rune_orb_loop' },
  [SpriteId.BossWarlord]: { [ClipId.Idle]: 'boss_warlord_idle', [ClipId.Run]: 'boss_warlord_walk', [ClipId.Cast]: 'boss_warlord_cast', [ClipId.Hurt]: 'boss_warlord_hurt', [ClipId.Death]: 'boss_warlord_death' },
  [SpriteId.BossSkullKing]: { [ClipId.Idle]: 'boss_skullking_idle', [ClipId.Cast]: 'boss_skullking_cast', [ClipId.Hurt]: 'boss_skullking_hurt', [ClipId.Death]: 'boss_skullking_death' },
  // Champions built from the small-fry sheets, scaled up and tinted by the renderer.
  [SpriteId.BossColossus]: { [ClipId.Idle]: 'slime_idle', [ClipId.Run]: 'slime_run', [ClipId.Cast]: 'slime_run', [ClipId.Death]: 'slime_death' },
  [SpriteId.BossMatriarch]: { [ClipId.Idle]: 'bat_idle', [ClipId.Run]: 'bat_run', [ClipId.Cast]: 'bat_run', [ClipId.Death]: 'bat_death' },
  [SpriteId.BossSentinel]: { [ClipId.Idle]: 'gargoyle_idle', [ClipId.Cast]: 'gargoyle_cast', [ClipId.Hurt]: 'gargoyle_hurt', [ClipId.Death]: 'gargoyle_death' },
  [SpriteId.BossHexweaver]: { [ClipId.Idle]: 'boss_hexweaver_idle', [ClipId.Run]: 'boss_hexweaver_run', [ClipId.Cast]: 'boss_hexweaver_cast', [ClipId.Hurt]: 'boss_hexweaver_hurt', [ClipId.Death]: 'boss_hexweaver_death' },
  [SpriteId.BossAshmaw]: { [ClipId.Idle]: 'boss_ashmaw_idle', [ClipId.Run]: 'boss_ashmaw_run', [ClipId.Cast]: 'boss_ashmaw_cast', [ClipId.Hurt]: 'boss_ashmaw_hurt', [ClipId.Death]: 'boss_ashmaw_death' },
  [SpriteId.BossBride]: { [ClipId.Idle]: 'boss_bride_idle', [ClipId.Run]: 'boss_bride_run', [ClipId.Cast]: 'boss_bride_cast', [ClipId.Hurt]: 'boss_bride_hurt', [ClipId.Death]: 'boss_bride_death' },
  [SpriteId.BossRubble]: { [ClipId.Idle]: 'fx_rune_orb_loop' },
  [SpriteId.BossAcid]: { [ClipId.Idle]: 'fx_rune_orb_loop' },
  [SpriteId.BossSkull]: { [ClipId.Idle]: 'skull_idle' },
  [SpriteId.BoltWand]: { [ClipId.Idle]: 'fx_wand_bolt_loop' },
  [SpriteId.BoltFire]: { [ClipId.Idle]: 'fx_fireball_loop' },
  [SpriteId.BoltPhase]: { [ClipId.Idle]: 'fx_phase_arrow_loop' },
  [SpriteId.Shockwave]: { [ClipId.Idle]: 'shockwave' },
  [SpriteId.PickupCleaver]: { [ClipId.Idle]: 'pickup_cleaver' },
  [SpriteId.PickupLightning]: { [ClipId.Idle]: 'pickup_lightning' },
  [SpriteId.PickupCrossbow]: { [ClipId.Idle]: 'pickup_crossbow' },
  [SpriteId.PickupBomb]: { [ClipId.Idle]: 'pickup_bomb_idle' },
  [SpriteId.PickupShield]: { [ClipId.Idle]: 'pickup_shield_idle' },
  [SpriteId.WardBlade]: { [ClipId.Idle]: 'ward_blade_loop' },
  [SpriteId.Mine]: { [ClipId.Idle]: 'mine_idle' },
  [SpriteId.Gem]: { [ClipId.Idle]: 'gem' },
  [SpriteId.PrizeCabbage]: { [ClipId.Idle]: 'prize_cabbage' },
  [SpriteId.PrizeHat]: { [ClipId.Idle]: 'prize_hat' },
  [SpriteId.PrizeChalice]: { [ClipId.Idle]: 'prize_chalice' },
  [SpriteId.PrizeGoldChest]: { [ClipId.Idle]: 'prize_gold_chest' },
  [SpriteId.PrizeSpellbook]: { [ClipId.Idle]: 'prize_spellbook' },
  [SpriteId.PrizeCrystalBall]: { [ClipId.Idle]: 'prize_crystal_ball' },
  [SpriteId.PrizePotion]: { [ClipId.Idle]: 'prize_potion' },
  [SpriteId.PrizeHourglass]: { [ClipId.Idle]: 'prize_hourglass' },
  [SpriteId.Ghost]: { [ClipId.Idle]: 'ghost' },
  [SpriteId.Shadow]: {},
  [SpriteId.MuzzleWand]: { [ClipId.Idle]: 'fx_muzzle_wand_burst' },
  [SpriteId.MuzzleFire]: { [ClipId.Idle]: 'fx_muzzle_fire_burst' },
  [SpriteId.MuzzlePhase]: { [ClipId.Idle]: 'fx_muzzle_phase_burst' },
  [SpriteId.Slash]: { [ClipId.Idle]: 'fx_slash_burst' },
  [SpriteId.LightningHit]: { [ClipId.Idle]: 'fx_lightning_hit_burst' },
  [SpriteId.Explosion]: { [ClipId.Idle]: 'fx_explosion_burst' },
  [SpriteId.BombBlast]: { [ClipId.Idle]: 'fx_bomb_burst' },
  [SpriteId.Goo]: { [ClipId.Idle]: 'fx_goo_burst' },
  [SpriteId.Smoke]: { [ClipId.Idle]: 'fx_smoke_burst' },
  [SpriteId.Spark]: { [ClipId.Idle]: 'fx_spark_burst' },
  [SpriteId.Torch]: { [ClipId.Idle]: 'fx_torch_loop' },
  [SpriteId.FlameJet]: { [ClipId.Idle]: 'fx_breath_loop' },
  [SpriteId.COUNT]: {},
};

/** Fallback durations (seconds) for one-shot FX when the atlas is missing. */
const FALLBACK_ONESHOT: Partial<Record<SpriteId, number>> = {
  [SpriteId.MuzzleWand]: 0.16,
  [SpriteId.MuzzleFire]: 0.18,
  [SpriteId.MuzzlePhase]: 0.16,
  [SpriteId.Slash]: 0.22,
  [SpriteId.LightningHit]: 0.16,
  [SpriteId.Explosion]: 0.5,
  [SpriteId.BombBlast]: 0.48,
  [SpriteId.Goo]: 0.3,
  [SpriteId.Smoke]: 0.5,
  [SpriteId.Spark]: 0.16,
  [SpriteId.FlameJet]: 0.4,
};
const FALLBACK_DEATH = 0.52;

/** Sprites whose own sheet may be missing borrow another sprite's frames wholesale. */
const ART_FALLBACK: Partial<Record<SpriteId, SpriteId>> = {
  [SpriteId.BossWarlord]: SpriteId.Golem,
  [SpriteId.BossSkullKing]: SpriteId.Skull,
  [SpriteId.Wraith]: SpriteId.Ghost,
  [SpriteId.Hexer]: SpriteId.Imp,
  [SpriteId.Cinder]: SpriteId.Skull,
  [SpriteId.Blight]: SpriteId.Slime,
  [SpriteId.Brute]: SpriteId.Golem,
  [SpriteId.Spitter]: SpriteId.Gargoyle,
  [SpriteId.Mimic]: SpriteId.PrizeGoldChest,
  [SpriteId.BossHexweaver]: SpriteId.Imp,
  [SpriteId.BossAshmaw]: SpriteId.Skull,
  [SpriteId.BossBride]: SpriteId.Ghost,
};

const PROCEDURAL_FRAME: FrameInfo = { u0: 0, v0: 0, u1: 1, v1: 1, w: 1, h: 1, ox: 0, oy: 0, ppu: 1 };

/**
 * Sprite -> frame table -> clip ranges, built from the atlas manifest at boot.
 * Without an atlas every sprite gets a single procedural frame and synthetic
 * clip timings so gameplay (death delays, FX lifetimes) still works.
 */
export class AnimRegistry {
  private readonly frames: FrameInfo[][] = [];
  private readonly clips: ClipInfo[] = [];
  private readonly ownClip = new Uint8Array(SpriteId.COUNT * ClipId.COUNT);
  hasAtlas = false;

  constructor() {
    this.reset();
  }

  private reset(): void {
    this.frames.length = 0;
    this.clips.length = 0;
    this.ownClip.fill(0);
    for (let s = 0; s < SpriteId.COUNT; s++) {
      this.frames.push([PROCEDURAL_FRAME]);
      for (let c = 0; c < ClipId.COUNT; c++) {
        const oneShot = FALLBACK_ONESHOT[s as SpriteId];
        // Hurt must finish even without art or the gameplay lock on it never releases.
        const duration = c === ClipId.Death ? FALLBACK_DEATH : c === ClipId.Hurt ? 0.25 : oneShot ?? 1;
        const loop = c !== ClipId.Death && c !== ClipId.Hurt && oneShot === undefined;
        this.clips.push({ start: 0, len: 1, fps: 1 / duration, loop, duration });
      }
    }
  }

  load(manifest: AtlasManifestJson): void {
    this.reset();
    const size = manifest.meta.size;
    const anims = manifest.anims ?? {};
    const defined = new Array<boolean>(ClipId.COUNT);
    for (let s = 0; s < SpriteId.COUNT; s++) {
      const names = CLIP_NAMES[s as SpriteId];
      const table: FrameInfo[] = [];
      defined.fill(false);
      for (let c = 0; c < ClipId.COUNT; c++) {
        const animName = names[c as ClipId];
        const anim = animName ? anims[animName] : undefined;
        if (!anim) continue;
        const start = table.length;
        for (const fname of anim.frames) {
          const f = manifest.frames[fname];
          if (!f) continue;
          table.push({
            u0: f.x / size,
            v0: 1 - (f.y + f.h) / size,
            u1: (f.x + f.w) / size,
            v1: 1 - f.y / size,
            w: f.w,
            h: f.h,
            ox: f.ox ?? 0,
            oy: f.oy ?? 0,
            ppu: anim.ppu,
          });
        }
        const len = table.length - start;
        if (len === 0) continue;
        const fps = anim.fps > 0 ? anim.fps : 1;
        this.clips[s * ClipId.COUNT + c] = { start, len, fps, loop: anim.loop, duration: len / fps };
        defined[c] = true;
        this.ownClip[s * ClipId.COUNT + c] = 1;
      }
      if (!defined[ClipId.Idle]) continue;
      this.frames[s] = table;
      // Clips without art borrow Idle so the sprite still draws; one-shots end after a pass.
      const idle = this.clips[s * ClipId.COUNT + ClipId.Idle];
      for (let c = 0; c < ClipId.COUNT; c++) {
        if (defined[c]) continue;
        const oneShot = c === ClipId.Death || c === ClipId.Hurt;
        const dur = oneShot ? (c === ClipId.Death ? FALLBACK_DEATH : 0.25) : idle.duration;
        this.clips[s * ClipId.COUNT + c] = { start: idle.start, len: idle.len, fps: idle.len / dur, loop: !oneShot, duration: dur };
      }
    }
    for (const key in ART_FALLBACK) {
      const s = Number(key) as SpriteId;
      const from = ART_FALLBACK[s];
      if (from === undefined || this.hasArt(s) || !this.hasArt(from)) continue;
      this.frames[s] = this.frames[from];
      for (let c = 0; c < ClipId.COUNT; c++) {
        this.clips[s * ClipId.COUNT + c] = this.clips[from * ClipId.COUNT + c];
        this.ownClip[s * ClipId.COUNT + c] = this.ownClip[from * ClipId.COUNT + c];
      }
    }
    this.hasAtlas = true;
  }

  /** Whether the sprite has real atlas art (vs the procedural fallback frame). */
  hasArt(sprite: SpriteId): boolean {
    return this.frames[sprite].length > 1 || this.frames[sprite][0] !== PROCEDURAL_FRAME;
  }

  /** True when this clip was packed in the atlas (or borrowed from a cousin that has it). */
  hasOwnClip(sprite: SpriteId, clip: ClipId): boolean {
    return this.ownClip[sprite * ClipId.COUNT + clip] === 1;
  }

  frameTable(sprite: SpriteId): readonly FrameInfo[] {
    return this.frames[sprite];
  }

  clip(sprite: SpriteId, clip: ClipId): ClipInfo {
    return this.clips[sprite * ClipId.COUNT + clip];
  }

  duration(sprite: SpriteId, clip: ClipId): number {
    return this.clips[sprite * ClipId.COUNT + clip].duration;
  }
}

export const Anims = new AnimRegistry();
